/**
 * PRODUCT_DEV-679: 未保存の編集を失わせないためのガード（React フック）。
 *
 * これまでリポジトリ全体に `beforeunload` / `useBlocker` が 1 件も無く、
 * プリセット編集や辞書編集で「戻る」を押すと編集内容が黙って消えていた。
 *
 * アプリ内遷移（react-router）とブラウザ離脱（リロード / タブ閉じ）の 2 経路を
 * 1 フックで塞ぐ。判定ロジックは `unsavedChangesGuard.ts`（React 非依存・テスト有）。
 *
 * 前提: `createBrowserRouter` などの data router 配下でのみ `useBlocker` が働く。
 */
import { useCallback, useEffect, useRef } from "react";
import { useBlocker, type BlockerFunction } from "react-router";
import {
  attachBeforeUnloadGuard,
  shouldBlockNavigation,
} from "./unsavedChangesGuard";

export interface UseUnsavedChangesGuardResult {
  /** 遷移がブロックされ、確認ダイアログを出すべき状態か */
  isBlocked: boolean;
  /** 破棄して遷移する */
  confirmLeave: () => void;
  /** 現在の画面に留まる */
  cancelLeave: () => void;
  /**
   * 次の 1 回だけ遷移を通す（呼んだ直後に `navigate()` すること）。
   *
   * 「編集内容を別の場所へ退避したうえで意図的に離れる」導線用。
   * 例: プリセット編集の「このプロンプトで試す」は form を sessionStorage へ
   * 書いてから録音画面へ飛ぶので、確認ダイアログを出す意味がない。
   *
   * dirty state は非同期に反映されるため、`setState` してから `navigate()` しても
   * ブロック判定には間に合わない。ref による同期的なバイパスが必要。
   *
   * 注意: 呼んだあと遷移が起きなければフラグは立ったまま残り、次の遷移が
   * 1 回だけ素通りする。必ず遷移とセットで呼ぶこと。
   */
  allowNextNavigation: () => void;
}

/**
 * 未保存の変更がある間、画面離脱に確認を挟む。
 *
 * @param isDirty 未保存の変更があるか
 *
 * @example
 * ```tsx
 * const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesGuard(isDirty);
 * // isBlocked のとき確認ダイアログを描画し、破棄=confirmLeave / 留まる=cancelLeave
 * ```
 */
export function useUnsavedChangesGuard(
  isDirty: boolean,
): UseUnsavedChangesGuardResult {
  // beforeunload リスナは張り替えずに ref 経由で最新の dirty を読む
  // （張り替えの隙間に unload されると確認が出ないため）。
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  // allowNextNavigation() 用のワンショットフラグ
  const bypassOnceRef = useRef(false);

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      if (bypassOnceRef.current) {
        bypassOnceRef.current = false;
        return false;
      }
      return shouldBlockNavigation(isDirtyRef.current, {
        currentLocation,
        nextLocation,
      });
    },
    // isDirty は ref 経由で読むので依存に入れない。react-router は
    // shouldBlock の同一性が変わると blocker を作り直すため、安定させたい。
    [],
  );

  const blocker = useBlocker(shouldBlock);

  useEffect(
    () => attachBeforeUnloadGuard(window, () => isDirtyRef.current),
    [],
  );

  // 保存などで dirty が解消したのにブロック中のままだと、ダイアログが残り続ける。
  useEffect(() => {
    if (!isDirty && blocker.state === "blocked") blocker.reset();
  }, [isDirty, blocker]);

  const confirmLeave = useCallback(() => {
    if (blocker.state === "blocked") blocker.proceed();
  }, [blocker]);

  const cancelLeave = useCallback(() => {
    if (blocker.state === "blocked") blocker.reset();
  }, [blocker]);

  const allowNextNavigation = useCallback(() => {
    bypassOnceRef.current = true;
  }, []);

  return {
    isBlocked: blocker.state === "blocked",
    confirmLeave,
    cancelLeave,
    allowNextNavigation,
  };
}
